import { useState } from "react";
import { api } from "../api";
import SEO from "../components/SEO";

const dropdownOptions = [
  "Plan a health campaign",
  "Sponsor a podcast series",
  "Book PR & media support",
  "Collaborate with The Health Plug",
  "General enquiry",
];

const socials = [
  { label: "Instagram", icon: "📸", href: "#" },
  { label: "TikTok",    icon: "🎵", href: "#" },
  { label: "Facebook",  icon: "💬", href: "#" },
  { label: "LinkedIn",  icon: "💼", href: "#" },
  { label: "YouTube",   icon: "▶️",  href: "#" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", campaign_type: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.email) { setError("Name and email are required."); return; }
    setLoading(true); setError("");
    try {
      await api.submitContact(form);
      setSubmitted(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <>
    <SEO
      title="Contact Us"
      path="/contact"
      description="Get in touch with The Health Plug. Plan a health campaign, book PR and media support, or enquire about youth health communications, podcast production and community health activations."
    />
    <div style={{ paddingTop: "5rem" }}>
      {/* Hero */}
      <section style={{ background: "#8A1613", padding: "6rem 2rem 5rem", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          {["CONNECT","COLLABORATE","COMMUNICATE"].map((w,i) => (
            <span key={i} style={{ position: "absolute", fontFamily: "var(--font)", fontWeight: 900, fontSize: "clamp(4rem,10vw,10rem)", color: "transparent", WebkitTextStroke: "1.5px rgba(255,255,255,0.08)", top: `${i*35}%`, left: `${i*25}%`, whiteSpace: "nowrap", userSelect: "none" }}>{w}</span>
          ))}
        </div>
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <p className="section-label" style={{ color: "rgba(255,255,255,0.6)" }}>Contact</p>
          <h1 style={{ fontFamily: "var(--font)", fontWeight: 900, fontSize: "clamp(2.8rem, 7vw, 6rem)", color: "#fff", lineHeight: 0.95, letterSpacing: "-0.03em", marginBottom: "1.25rem" }}>
            Let's Work<br />Together.
          </h1>
          <p style={{ color: "rgba(255,255,255,0.65)", maxWidth: 480, fontFamily: "var(--font-body)", fontSize: "1.05rem", lineHeight: 1.75 }}>
            Planning a health campaign, podcast series, community activation or PR project? Let's talk.
          </p>
        </div>
      </section>

      {/* Form + details */}
      <section style={{ background: "#F7F1DF", padding: "6rem 2rem" }}>
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "4rem", alignItems: "start" }} className="mobile-full">

            {/* Form */}
            <div style={{ background: "#fff", borderRadius: 24, padding: "2.5rem", border: "1px solid rgba(11,11,11,0.07)" }}>
              {submitted ? (
                <div style={{ textAlign: "center", padding: "3rem 0" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#007965", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem", margin: "0 auto 1.25rem" }}>✅</div>
                  <h3 style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.5rem", color: "#0B0B0B", marginBottom: "0.5rem" }}>Enquiry Sent!</h3>
                  <p style={{ fontFamily: "var(--font-body)", color: "rgba(11,11,11,0.55)" }}>We'll be in touch within 48 hours. Watch your inbox — including your spam folder.</p>
                </div>
              ) : (
                <>
                  <h2 style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.5rem", color: "#0B0B0B", marginBottom: "2rem" }}>Send an Enquiry</h2>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    {[
                      { name: "name",    placeholder: "Your Name *",          type: "text",  span: false },
                      { name: "company", placeholder: "Organisation",          type: "text",  span: false },
                      { name: "email",   placeholder: "Email Address *",       type: "email", span: false },
                      { name: "phone",   placeholder: "Phone / WhatsApp",      type: "tel",   span: false },
                    ].map(f => (
                      <input key={f.name} name={f.name} placeholder={f.placeholder} type={f.type} value={form[f.name]} onChange={handle}
                        className="form-input-dark" style={{ gridColumn: f.span ? "1/-1" : "auto" }} />
                    ))}

                    <select name="campaign_type" value={form.campaign_type} onChange={handle} className="form-input-dark" style={{ gridColumn: "1/-1" }}>
                      <option value="">What do you need help with?</option>
                      {dropdownOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>

                    <textarea name="message" placeholder="Tell us more..." value={form.message} onChange={handle} rows={5}
                      className="form-input-dark" style={{ gridColumn: "1/-1", resize: "vertical" }} />

                    {error && <p style={{ gridColumn: "1/-1", color: "#8A1613", fontSize: "0.85rem", fontFamily: "var(--font-body)" }}>{error}</p>}

                    <div style={{ gridColumn: "1/-1" }}>
                      <button className="btn-red" onClick={submit} disabled={loading} style={{ width: "100%", padding: "1rem" }}>
                        {loading ? "Sending..." : "Send Enquiry"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Contact details */}
            <div>
              <h3 style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.3rem", color: "#0B0B0B", marginBottom: "2rem" }}>Get in touch directly</h3>

              {[
                { icon: "📧", label: "Email", val: "info@thehealthplug.co.za", href: "mailto:info@thehealthplug.co.za" },
                { icon: "📱", label: "Phone / WhatsApp", val: "Available on request", href: null },
                { icon: "📍", label: "Location", val: "South Africa", href: null },
                { icon: "🗓", label: "Schedule a Call", val: "Book a time that works for you", href: "#" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", alignItems: "flex-start" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "#8A1613", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <p style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.78rem", color: "rgba(11,11,11,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.2rem" }}>{item.label}</p>
                    {item.href
                      ? <a href={item.href} style={{ fontFamily: "var(--font-body)", color: "#8A1613", fontSize: "0.95rem", fontWeight: 500 }}>{item.val}</a>
                      : <p style={{ fontFamily: "var(--font-body)", color: "#0B0B0B", fontSize: "0.95rem" }}>{item.val}</p>
                    }
                  </div>
                </div>
              ))}

              {/* Socials */}
              <div style={{ marginTop: "2rem", paddingTop: "2rem", borderTop: "1px solid rgba(11,11,11,0.08)" }}>
                <p style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.78rem", color: "rgba(11,11,11,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "1rem" }}>Follow the Plug</p>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  {socials.map(s => (
                    <a key={s.label} href={s.href} title={s.label} style={{ width: 44, height: 44, borderRadius: 12, background: "#0B0B0B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", transition: "background 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#8A1613"}
                      onMouseLeave={e => e.currentTarget.style.background = "#0B0B0B"}
                    >{s.icon}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
